import { buildSearchIntent, displaySearchTerm, matchesSearchIntent, canonicalSupplier } from '@theme/fastutv-search-intent';
import { enhanceSupplierFacets } from '@theme/fastutv-supplier-facets';

const collator = new Intl.Collator(document.documentElement.lang || 'en', { numeric: true, sensitivity: 'base' });
const localSorts = new Set(['vendor-ascending', 'vendor-descending', 'title-ascending', 'title-descending']);
const supportedNativeFilters = new Set(['filter.p.vendor','filter.p.product_type','filter.v.availability','filter.v.price.gte','filter.v.price.lte']);
const customParams = ['fastutv_sort','fastutv_supplier','fastutv_type','fastutv_available','fastutv_min','fastutv_max','fastutv_page'];
const wait = (ms, signal) => new Promise((resolve, reject) => {
  const timer = setTimeout(resolve, ms);
  signal.addEventListener('abort', () => { clearTimeout(timer); reject(new DOMException('Aborted','AbortError')); }, { once:true });
});

class FastUTVDiscovery extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    this.config = JSON.parse(this.querySelector('[data-discovery-config]').textContent);
    this.s = this.config.strings;
    this.url = new URL(location.href);
    this.raw = this.config.kind === 'search' ? displaySearchTerm(this.url) : '';
    this.intent = buildSearchIntent(this.raw);
    if (this.config.kind === 'collection') {
      const tag = (this.config.tags || []).find(value => /^Browse Model:/i.test(value));
      if (tag) this.intent = buildSearchIntent(tag.replace(/^Browse Model:\s*/i,''));
    }
    this.sort = this.url.searchParams.get('fastutv_sort') || this.config.native_sort;
    this.querySelector('[data-discovery-sort]').value = this.sort;
    this.state = {
      supplier:this.url.searchParams.get('fastutv_supplier') || this.config.suppliers.length && [...new Set(this.config.suppliers.map(canonicalSupplier))].join('|') || '',
      type:this.url.searchParams.get('fastutv_type') || this.url.searchParams.getAll('filter.p.product_type').join('|'),
      available:this.url.searchParams.get('fastutv_available') || (this.url.searchParams.getAll('filter.v.availability').length===1?this.url.searchParams.get('filter.v.availability'):'') || '',
      min:this.url.searchParams.get('fastutv_min') || this.url.searchParams.get('filter.v.price.gte') || '',
      max:this.url.searchParams.get('fastutv_max') || this.url.searchParams.get('filter.v.price.lte') || '',
      page:Math.max(1, Number(this.url.searchParams.get('fastutv_page')) || 1)
    };
    if(this.raw&&this.config.kind==='search')document.title=this.s.search_title.replace('[query]',this.raw);
    const label = [this.intent.model?.label || this.intent.make?.label, this.intent.category?.label].filter(Boolean).join(' · ');
    const intentNode = this.querySelector('[data-discovery-intent]');
    intentNode.textContent = label;
    intentNode.hidden = !label;
    this.addEventListener('change', event => this.change(event), { signal:this.controller.signal });
    this.addEventListener('click', event => this.click(event), { signal:this.controller.signal });
    window.addEventListener('popstate', () => location.reload(), { signal:this.controller.signal });
    if (this.intent.scoped || localSorts.has(this.sort) || customParams.some(key => this.url.searchParams.has(key))) this.activate();
    enhanceSupplierFacets(this.closest('results-list') || document);
    this.observer = new MutationObserver(() => {
      if (this.aliasFrame) return;
      this.aliasFrame = requestAnimationFrame(() => {
        this.aliasFrame = 0;
        if (this.isConnected) enhanceSupplierFacets(this.closest('results-list') || document);
      });
    });
    this.observer.observe(this.closest('results-list') || this, { childList:true, subtree:true });
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.fetchController?.abort();
    this.observer?.disconnect();
    if (this.aliasFrame) cancelAnimationFrame(this.aliasFrame);
    this.controller = null;
  }

  async activate() {
    this.setAttribute('data-active','');
    this.setAttribute('aria-busy','true');
    this.querySelector('[data-discovery-status]').textContent = this.s.loading;
    this.querySelector('[data-discovery-retry]').hidden = true;
    this.querySelector('[data-discovery-filters]').hidden = true;
    this.querySelector('[data-discovery-grid]').hidden = true;
    this.querySelector('[data-discovery-pages]').hidden = true;
    this.querySelector('[data-discovery-fitment]').hidden = !this.intent.model;
    this.fetchController?.abort();
    const controller = new AbortController();
    this.fetchController = controller;
    try {
      const base = new URL(this.url);
      base.hash = '';
      base.searchParams.set('view','fastutv-discovery');
      base.searchParams.delete('sections');
      base.searchParams.delete('section_id');
      base.searchParams.delete('page');
      base.searchParams.delete('options');
      for(const key of supportedNativeFilters)base.searchParams.delete(key);
      for (const key of customParams) base.searchParams.delete(key);
      for(const [key,value] of [['filter.v.price.gte',this.state.min],['filter.v.price.lte',this.state.max]])if(value!=='')base.searchParams.set(key,value);
      if (this.config.kind === 'search') {
        base.searchParams.set('type','product');
        base.searchParams.set('q',this.intent.query || this.raw);
        if (this.intent.scoped) base.searchParams.set('options[prefix]','none');
      }
      const readPage = async page => {
        const request = new URL(base);
        request.searchParams.set('page',String(page));
        const response = await fetch(request, { signal:controller.signal, credentials:'same-origin', headers:{ Accept:'text/html' } });
        if (!response.ok) throw new Error('Native results HTTP '+response.status);
        const data = JSON.parse(await response.text());
        if (!Array.isArray(data.products) || !Number.isInteger(data.total) || !Number.isInteger(data.pages)) throw new Error('Invalid native result response');
        return data;
      };
      const first = await readPage(1);
      if(first.page !== 1 || first.pages !== Math.max(1,Math.ceil(first.total/250)))throw new Error('Unexpected native pagination');
      if (first.total > 25000 || first.pages > 100) throw new Error('Native result boundary exceeded');
      const products = [...first.products];
      for (let page=2;page<=first.pages;page++) {
        await wait(500,controller.signal);
        const data = await readPage(page);
        if (data.total !== first.total || data.pages !== first.pages || data.page !== page) throw new Error('Native results changed during loading');
        products.push(...data.products);
      }
      const unique = new Map(products.map(product => [String(product.id),product]));
      if (unique.size !== first.total) throw new Error('Incomplete native result set');
      if (controller.signal.aborted || !this.isConnected) return;
      for(const product of unique.values()) {
        // Present variant evidence can prove inclusion. Missing evidence still
        // requires every native variant page before excluding a capped product.
        if(this.intent.model && product.variant_count > product.variants.length && !matchesSearchIntent(product,{...this.intent,category:null})) {
          const variantURL=new URL(product.url,location.origin);
          if(variantURL.origin!==location.origin)throw new Error('Invalid product origin');
          variantURL.searchParams.set('view','fastutv-discovery');
          const preview=this.url.searchParams.get('preview_theme_id');if(preview)variantURL.searchParams.set('preview_theme_id',preview);
          const variants=new Map();let page=1;let pages=1;
          do {
            await wait(500,controller.signal);
            variantURL.searchParams.set('page',String(page));
            const response=await fetch(variantURL,{signal:controller.signal,credentials:'same-origin'});
            if(!response.ok)throw new Error('Native variant results HTTP '+response.status);
            const data=JSON.parse(await response.text());
            if(data.total!==product.variant_count||data.page!==page||data.pages!==Math.max(1,Math.ceil(data.total/50))||!Array.isArray(data.variants)||data.pages>50)throw new Error('Incomplete native variant metadata');
            pages=data.pages;for(const variant of data.variants)variants.set(String(variant.id),variant);page++;
          }while(page<=pages);
          if(variants.size!==product.variant_count)throw new Error('Incomplete native variant set');
          product.variants=[...variants.values()].map(variant=>variant.title);
          product.variant_prices=[...variants.values()].map(variant=>variant.price);
        }
      }
      if(controller.signal.aborted||!this.isConnected)return;
      this.products = [...unique.values()].filter(product => matchesSearchIntent(product,this.intent));
      this.querySelector('[data-discovery-filters]').hidden = false;
      this.querySelector('[data-discovery-grid]').hidden = false;
      this.querySelector('[data-discovery-availability]').value = this.state.available;
      this.querySelector('[data-discovery-min]').value = this.state.min;
      this.querySelector('[data-discovery-max]').value = this.state.max;
      this.render();
    } catch (error) {
      if (error.name === 'AbortError') return;
      this.querySelector('[data-discovery-status]').textContent = this.s.error;
      this.querySelector('[data-discovery-retry]').hidden = false;
      this.dispatchEvent(new CustomEvent('fastutv:discovery-error',{bubbles:true,detail:{message:error.message}}));
    } finally {
      if (this.fetchController === controller) this.removeAttribute('aria-busy');
    }
  }

  priceMatches(product) {
    const min = this.state.min === '' ? -Infinity : Number(this.state.min)*100;
    const max = this.state.max === '' ? Infinity : Number(this.state.max)*100;
    if(product.variant_count > (product.variant_prices || []).length)return true;
    return (product.variant_prices || [product.price]).some(price => Number(price) >= min && Number(price) <= max);
  }

  filtered(ignore) {
    return this.products.filter(product =>
      (ignore === 'supplier' || !this.state.supplier || this.state.supplier.split('|').includes(canonicalSupplier(product.vendor))) &&
      (ignore === 'type' || !this.state.type || this.state.type.split('|').includes(product.product_type)) &&
      (!this.state.available || String(Number(product.available)) === this.state.available) &&
      this.priceMatches(product));
  }

  fillOptions(selector, options, empty, selected) {
    const select = this.querySelector(selector);
    const counts = new Map();
    for (const value of options) if (value) counts.set(value,(counts.get(value)||0)+1);
    if (selected && !counts.has(selected)) counts.set(selected, selected.split('|').reduce((sum,value)=>sum+(counts.get(value)||0),0));
    const nodes = [new Option(empty,'')];
    for (const [value,count] of [...counts].sort(([a],[b])=>collator.compare(a,b))) nodes.push(new Option(value.split('|').join(' + ')+' ('+count+')',value));
    select.replaceChildren(...nodes);
    select.value = selected;
  }

  render() {
    this.fillOptions('[data-discovery-supplier]',this.filtered('supplier').map(product=>canonicalSupplier(product.vendor)),this.s.all_suppliers,this.state.supplier);
    this.fillOptions('[data-discovery-type]',this.filtered('type').map(product=>product.product_type),this.s.all_parts,this.state.type);
    const rows = this.filtered();
    const titleOrder = (a,b) => collator.compare(a.title,b.title) || String(a.id).localeCompare(String(b.id));
    if (this.sort === 'vendor-ascending' || this.sort === 'vendor-descending') {
      const direction = this.sort.endsWith('descending') ? -1 : 1;
      rows.sort((a,b)=>direction*collator.compare(canonicalSupplier(a.vendor),canonicalSupplier(b.vendor)) || titleOrder(a,b));
    } else if (this.sort === 'title-ascending' || this.sort === 'title-descending') {
      rows.sort((a,b)=>(this.sort.endsWith('descending')?-1:1)*titleOrder(a,b));
    } else if (this.sort === 'price-ascending' || this.sort === 'price-descending') {
      rows.sort((a,b)=>(this.sort.endsWith('descending')?-1:1)*(a.price-b.price)||titleOrder(a,b));
    }
    const pages = Math.max(1,Math.ceil(rows.length/24));
    this.state.page = Math.min(this.state.page,pages);
    const subset = rows.slice((this.state.page-1)*24,this.state.page*24);
    this.querySelector('[data-discovery-status]').textContent = rows.length ? this.s.count.replace('[count]',rows.length) : this.s.empty+' '+this.s.empty_help;
    const inherited=(this.config.native_filters||[]).filter(filter=>!supportedNativeFilters.has(filter.param)&&this.url.searchParams.has(filter.param)).flatMap(filter=>(filter.active||[]).map(value=>filter.label+': '+value.label));
    const inheritedNode=this.querySelector('[data-discovery-native-filters]');
    if(inheritedNode){inheritedNode.textContent=inherited.join(' · ');inheritedNode.hidden=!inherited.length;}
    this.querySelector('[data-discovery-grid]').replaceChildren(...subset.map(product=>this.card(product)));
    const nav = this.querySelector('[data-discovery-pages]');
    nav.hidden = pages<=1;
    const buttons = [];
    const add = (label,page,current=false) => {
      const button=document.createElement('button');
      button.type='button'; button.textContent=label; button.dataset.discoveryPage=page;
      if(current) button.setAttribute('aria-current','page');
      buttons.push(button);
    };
    if(this.state.page>1)add(this.s.previous,this.state.page-1);
    const choices=new Set([1,pages,this.state.page-1,this.state.page,this.state.page+1]);
    for(const page of [...choices].sort((a,b)=>a-b)) if(page>=1&&page<=pages)add(String(page),page,page===this.state.page);
    if(this.state.page<pages)add(this.s.next,this.state.page+1);
    nav.replaceChildren(...buttons);
  }

  card(product) {
    const li=document.createElement('li'); li.className='fastutv-discovery__card'; li.dataset.productId=product.id; li.dataset.supplier=canonicalSupplier(product.vendor); li.dataset.price=product.price;
    const link=document.createElement('a');
    const url=new URL(product.url,location.origin);
    if(url.origin!==location.origin) return li;
    link.href=url.pathname+url.search;
    const media=document.createElement('div'); media.className='fastutv-discovery__image';
    if(product.image){
      const img=document.createElement('img');img.src=product.image;img.alt=product.image_alt||product.title;img.width=product.image_width;img.height=product.image_height;img.loading='lazy';img.decoding='async';
      try {
        const source=new URL(product.image,location.origin);
        if(source.origin===location.origin&&source.pathname.startsWith('/cdn/shop/')) {
          img.srcset=[240,360,480,720].map(width=>{const candidate=new URL(source);candidate.searchParams.set('width',String(width));return candidate.href+' '+width+'w';}).join(', ');
          img.sizes='auto, (min-width: 1440px) 329px, (min-width: 1101px) calc((100vw - 124px) / 4), (min-width: 750px) calc((100vw - 104px) / 3), calc((100vw - 44px) / 2)';
        }
      } catch { /* Keep the native image URL as the fallback. */ }
      media.append(img);
    }
    link.append(media);
    if(product.vendor){const vendor=document.createElement('p');vendor.className='fastutv-discovery__vendor';vendor.textContent=canonicalSupplier(product.vendor);link.append(vendor);}
    const heading=document.createElement('h3');heading.className='fastutv-discovery__title';heading.textContent=product.title;link.append(heading);
    const price=document.createElement('p');price.className='fastutv-discovery__price-text';price.textContent=(product.price_varies?this.s.from+' ':'')+product.price_text;link.append(price);
    const availability=document.createElement('p');availability.className='fastutv-discovery__availability';availability.textContent=product.available?this.s.in_stock:this.s.unavailable;link.append(availability);
    li.append(link); return li;
  }

  change(event) {
    if(event.target.matches('[data-discovery-sort]')){
      this.sort=event.target.value;
      if(!this.hasAttribute('data-active')&&!localSorts.has(this.sort)){
        const next=new URL(location.href); next.searchParams.set('sort_by',this.sort);next.searchParams.delete('page');next.searchParams.delete('fastutv_sort');location.assign(next.href);return;
      }
      this.state.page=1;
      this.save();
      if(!this.products){this.activate();return;}
      if(!localSorts.has(this.sort)&&!['price-ascending','price-descending','relevance'].includes(this.sort)){
        this.url.searchParams.set('sort_by',this.sort);this.activate();return;
      }
      if(this.sort==='relevance'){this.url.searchParams.set('sort_by','relevance');this.activate();return;}
      this.render();return;
    }
    const mapping=[['supplier','supplier'],['type','type'],['availability','available'],['min','min'],['max','max']];
    for(const [attribute,key] of mapping) if(event.target.matches('[data-discovery-'+attribute+']')){this.state[key]=event.target.value;this.state.page=1;this.save();if(key==='min'||key==='max')this.activate();else this.render();break;}
  }

  click(event) {
    const retry=event.target.closest('[data-discovery-retry]');
    if(retry){this.activate();return;}
    if(event.target.closest('[data-discovery-clear]')){
      const hadPrice=!!this.state.min||!!this.state.max;
      Object.assign(this.state,{supplier:'',type:'',available:'',min:'',max:'',page:1});
      for(const name of ['availability','min','max'])this.querySelector('[data-discovery-'+name+']').value='';
      const reload=hadPrice||[...this.url.searchParams.keys()].some(key=>key.startsWith('filter.')&&!supportedNativeFilters.has(key));
      this.clearNative=true;this.save();
      if(reload){this.products=null;this.activate();}else this.render();return;
    }
    const button=event.target.closest('[data-discovery-page]');
    if(button){this.state.page=Number(button.dataset.discoveryPage);this.save();this.render();this.querySelector('[data-discovery-status]').scrollIntoView({block:'start',behavior:'instant'});this.querySelector('[data-discovery-sort]').focus({preventScroll:true});}
  }

  save() {
    const next=new URL(location.href);
    next.searchParams.delete('page');
    for(const key of customParams)next.searchParams.delete(key);
    next.searchParams.set('fastutv_sort',this.sort);
    if(!localSorts.has(this.sort))next.searchParams.set('sort_by',this.sort);
    for(const [key,param] of [['supplier','fastutv_supplier'],['type','fastutv_type'],['available','fastutv_available'],['min','fastutv_min'],['max','fastutv_max'],['page','fastutv_page']]){
      const value=this.state[key];if(value!==''&&!(key==='page'&&value===1))next.searchParams.set(param,String(value));
    }
    for(const key of [...next.searchParams.keys()])if(supportedNativeFilters.has(key)||(this.clearNative&&key.startsWith('filter.')))next.searchParams.delete(key);
    this.clearNative=false;
    history.pushState({},'',next);
    this.url=next;
  }
}
if(!customElements.get('fastutv-discovery'))customElements.define('fastutv-discovery',FastUTVDiscovery);
